import React, { type ReactElement } from 'react';
import clsx from 'clsx';
import { useParams } from 'react-router-dom';
import ExternalLink from 'atom-components/external-link';
import Heading from 'atom-components/heading';
import TooltipItem from 'atom-components/tooltip-item';
import MarkStatusBadge from 'feature-components/badges/mark-status-badge';
import { fileDownloadHref } from 'helpers/file-download.helpers';
import ActiveMarkerBadge from 'pages/marking/components/active-marker-badge';
import { markStatusMessage } from 'pages/marking/helpers/mark.helpers';
import { markersOfResponseParams } from 'pages/marking/helpers/request-params.helpers';
import { useCustomerUsers } from 'services/assist-api/entities/customer-user/customer-users';
import type { BaseCustomerUser } from 'services/assist-api/entities/customer-user/types';
import {
  type BaseMediaObject,
  MediaObjectType
} from 'services/assist-api/entities/media-object/types';
import type { MarkStatus } from 'services/assist-api/entities/test-instance-response-mark/types';
import { useTestInstanceResponseMarkers } from 'services/assist-api/entities/test-instance-response-marker/hooks';
import type { TestInstanceResponseMarkerWithCustomerUser } from 'services/assist-api/entities/test-instance-response-marker/types';
import { useAuthCustomer } from 'services/providers/auth.hooks';
import { isInitialLoad } from 'utils/check-query-status';
import styles from './info-section.module.scss';

type MediaObjectProps = {
  mediaObject: BaseMediaObject;
};

function MediaObject({ mediaObject }: MediaObjectProps): ReactElement {
  function displayMediaType(mediaType: MediaObjectType): string {
    switch (mediaType) {
      case MediaObjectType.TestRubric:
        return 'rubric';
      case MediaObjectType.TestInstanceResponse:
        return 'response';
      default:
        return 'ERROR: Unknown type';
    }
  }

  return (
    <div className={styles.block}>
      <TooltipItem
        tooltip={`View PDF for this ${displayMediaType(mediaObject.type)} in a new tab.`}
      >
        <ExternalLink
          href={fileDownloadHref(mediaObject.contentUrl)}
          className={clsx(
            styles.mediaObjectLink,
            {
              [`${styles.testRubricMediaObjectLink}`]:
                mediaObject.type === MediaObjectType.TestRubric
            },
            {
              [`${styles.testInstanceResponseMediaObjectLink}`]:
                mediaObject.type === MediaObjectType.TestInstanceResponse
            }
          )}
        >
          {mediaObject.displayName}
        </ExternalLink>
      </TooltipItem>
    </div>
  );
}

type ActiveMarkerProps = {
  isAdministrator: boolean;
  isActiveMarker: boolean;
  isAiMarked: boolean | undefined;
  activeMarker: TestInstanceResponseMarkerWithCustomerUser | undefined;
};

function ActiveMarker({
  isAdministrator,
  isActiveMarker,
  activeMarker,
  isAiMarked
}: ActiveMarkerProps): ReactElement {
  const { responseId } = useParams();
  const currentOrganisation = useAuthCustomer();

  // Get markers of the selected response.
  const testInstanceResponseMarkersQuery =
    useTestInstanceResponseMarkers<TestInstanceResponseMarkerWithCustomerUser>(
      markersOfResponseParams(Number(responseId))
    );

  const organisationUserIds = testInstanceResponseMarkersQuery.data?.json.map(
    (marker) => marker.user.id
  );

  // Get all organisation users who are markers of this response only.
  const organisationUsersQuery = useCustomerUsers({
    filters: {
      id: organisationUserIds,
      customer: currentOrganisation?.id
    },
    groups: ['id']
  });

  const isLoading = isInitialLoad(testInstanceResponseMarkersQuery, organisationUsersQuery);

  // Find the active marker organisation user if they exist.
  const organisationUser: undefined | BaseCustomerUser = organisationUsersQuery.data?.json.find(
    (orgUser) => orgUser.id === activeMarker?.user.id
  );

  const notActiveMarkerNote = isAdministrator
    ? 'as you are an admin, you will be made the active marker when the form is saved.'
    : 'you must be set as the active marker (by a marking admin) to make changes.';
  const tooltip = isActiveMarker
    ? 'You are the active marker of this mark - you can make changes.'
    : `You are not the active marker of this mark - ${notActiveMarkerNote}`;

  // We should display the Active Marker badge, regardless of whether there is an associated
  // organisation user (error details are displayed inside the badge).
  return (
    <div className={styles.block}>
      {isLoading && 'Loading Active Marker...'}
      {!isLoading && (
        <TooltipItem tooltip={tooltip}>
          <ActiveMarkerBadge
            displayWarning={!isActiveMarker}
            activeMarker={activeMarker}
            isAiMarked={isAiMarked}
            organisationUser={organisationUser}
          />
        </TooltipItem>
      )}
    </div>
  );
}

function CurrentMarkStatus({ children }: { children: MarkStatus }): ReactElement {
  return (
    <div className={styles.block}>
      <TooltipItem tooltip={markStatusMessage(children)}>
        <MarkStatusBadge>{children}</MarkStatusBadge>
      </TooltipItem>
    </div>
  );
}

type InfoSectionProps = {
  isAdministrator: boolean;
  isActiveMarker: boolean;
  isAiMarked: boolean | undefined;
  activeMarker: TestInstanceResponseMarkerWithCustomerUser | undefined;
  rubricMediaObjects: BaseMediaObject[];
  responseMediaObjects: BaseMediaObject[];
  markStatus: MarkStatus;
};

export default function InfoSection({
  isAdministrator,
  isActiveMarker,
  isAiMarked,
  activeMarker,
  rubricMediaObjects,
  responseMediaObjects,
  markStatus
}: InfoSectionProps): ReactElement {
  function renderSortedMediaObjects(mediaObjects: BaseMediaObject[]): ReactElement[] {
    return mediaObjects
      .sort((a, b) => a.order - b.order) // Todo: Convert to toSorted after Node 20 upgrade
      .map((mediaObject) => <MediaObject key={mediaObject.id} mediaObject={mediaObject} />);
  }

  return (
    <div className={styles.infoSection}>
      <Heading level={2}>Mark Response</Heading>
      <div className={styles.blocks}>
        <CurrentMarkStatus>{markStatus}</CurrentMarkStatus>
        <ActiveMarker
          isAdministrator={isAdministrator}
          isActiveMarker={isActiveMarker}
          activeMarker={activeMarker}
          isAiMarked={isAiMarked}
        />
        {renderSortedMediaObjects(rubricMediaObjects)}
        {renderSortedMediaObjects(responseMediaObjects)}
      </div>
    </div>
  );
}
